import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from './ui/sonner';
import { Send, User, Mail, Phone, MapPin, Clock, Briefcase, GraduationCap, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LeadForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    courseInterest: [],
    experienceLevel: '',
    preferredBatchTiming: '',
    placementSupport: false,
    location: ''
  });

  const [isCoursesOpen, setIsCoursesOpen] = useState(false);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isCoursesOpen && !event.target.closest('.course-dropdown')) {
        setIsCoursesOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCoursesOpen]);

  const courses = [
    'Single Software for Editing (Avid / FCPX / Premiere Pro, 2.5 to 3.5 months)',
    'Master in AI Video Creation (1 month)',
    'Master in Digital Color Grading (DaVinci Resolve, 3.5 months)',
    'AI Integrated Video Editing (4 months)',
    'Master in Film Editing (4 months)',
    'Master in Social Media Editing (8 months)',
    'Advanced Diploma in Film & Video Editing (11 months)',
    'Professional Diploma in Film & Video Editing (14 months, includes 2-month internship)'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!formData.name || !formData.email || !formData.phone || 
        formData.courseInterest.length === 0 || !formData.experienceLevel || 
        !formData.preferredBatchTiming || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (formData.courseInterest.length > 3) {
      toast.error('Please select up to 3 courses only');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Convert courseInterest array to string for backend
      const submissionData = {
        ...formData,
        courseInterest: formData.courseInterest.join(', ')
      };
      
      const response = await axios.post(`${API}/leads`, submissionData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          courseInterest: [],
          experienceLevel: '',
          preferredBatchTiming: '',
          placementSupport: false,
          location: ''
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      
      // Handle different error formats
      let errorMessage = 'Failed to submit your inquiry. Please try again or contact us via WhatsApp.';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array (validation errors)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || 'Validation error').join(', ');
        } 
        // If detail is a string
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // If detail is an object
        else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <section id="lead-form" className="py-16 md:py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-orange-500 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Start Your Journey
              <span className="block bg-gradient-to-r from-purple-400 to-orange-400 bg-clip-text text-transparent mt-2">
                We&apos;ll Help You Choose the Right Course
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Fill out the form below and our admissions counselors will get in touch within 24 hours
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/10 shadow-2xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white text-lg flex items-center gap-2">
                  <User className="h-4 w-4 text-purple-400" />
                  Full Name *
                </Label>
                <Input
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 h-12 rounded-xl focus:border-purple-400 focus:ring-purple-400"
                  placeholder="Enter your full name"
                />
              </div>

              {/* Email & Phone */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white text-lg flex items-center gap-2">
                    <Mail className="h-4 w-4 text-purple-400" />
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 h-12 rounded-xl focus:border-purple-400 focus:ring-purple-400"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white text-lg flex items-center gap-2">
                    <Phone className="h-4 w-4 text-purple-400" />
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 h-12 rounded-xl focus:border-purple-400 focus:ring-purple-400"
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
              </div>

              {/* Course Interest (Multi-select dropdown) & Experience Level */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-white text-lg flex items-center gap-2">
                    <GraduationCap className="h-4 w-4 text-purple-400" />
                    Interested Course * (Select up to 3)
                  </Label>
                  <div className="relative course-dropdown">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsCoursesOpen(!isCoursesOpen);
                      }}
                      className="w-full bg-white/10 border border-white/20 text-white h-12 rounded-xl px-4 text-left flex items-center justify-between focus:border-purple-400 focus:ring-2 focus:ring-purple-400 transition-all"
                    >
                      <span className="text-sm">
                        {formData.courseInterest.length === 0
                          ? 'Select courses...'
                          : `${formData.courseInterest.length} course${formData.courseInterest.length > 1 ? 's' : ''} selected`}
                      </span>
                      <svg className={`w-5 h-5 transition-transform ${isCoursesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {isCoursesOpen && (
                      <div className="absolute z-50 w-full mt-2 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl max-h-64 overflow-y-auto">
                        {courses.map((course, idx) => (
                          <div
                            key={idx}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (formData.courseInterest.includes(course)) {
                                handleChange('courseInterest', formData.courseInterest.filter(c => c !== course));
                              } else if (formData.courseInterest.length < 3) {
                                handleChange('courseInterest', [...formData.courseInterest, course]);
                              } else {
                                toast.error('You can select up to 3 courses only');
                              }
                            }}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-slate-700 cursor-pointer transition-colors"
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                              formData.courseInterest.includes(course) 
                                ? 'bg-purple-600 border-purple-600' 
                                : 'border-white/40'
                            }`}>
                              {formData.courseInterest.includes(course) && (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <span className="text-white text-sm">{course}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {formData.courseInterest.length > 0 && (
                    <p className="text-purple-300 text-xs mt-1">
                      {formData.courseInterest.length} of 3 selected
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="experience" className="text-white text-lg flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-purple-400" />
                    Experience Level *
                  </Label>
                  <Select value={formData.experienceLevel} onValueChange={(value) => handleChange('experienceLevel', value)} required>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white h-12 rounded-xl focus:border-purple-400 focus:ring-purple-400">
                      <SelectValue placeholder="Select experience" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 text-white">
                      <SelectItem value="beginner">Complete Beginner</SelectItem>
                      <SelectItem value="intermediate">Some Experience</SelectItem>
                      <SelectItem value="advanced">Advanced/Professional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Batch Timing & Location */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="timing" className="text-white text-lg flex items-center gap-2">
                    <Clock className="h-4 w-4 text-purple-400" />
                    Preferred Batch Timing *
                  </Label>
                  <Select value={formData.preferredBatchTiming} onValueChange={(value) => handleChange('preferredBatchTiming', value)} required>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white h-12 rounded-xl focus:border-purple-400 focus:ring-purple-400">
                      <SelectValue placeholder="Select timing" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 text-white">
                      <SelectItem value="morning">Morning (8 AM - 12 PM)</SelectItem>
                      <SelectItem value="afternoon">Afternoon (12 PM - 4 PM)</SelectItem>
                      <SelectItem value="evening">Evening (4 PM - 8 PM)</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location" className="text-white text-lg flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-purple-400" />
                    Your Location *
                  </Label>
                  <Input
                    id="location"
                    type="text"
                    required
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 h-12 rounded-xl focus:border-purple-400 focus:ring-purple-400"
                    placeholder="City/Area"
                  />
                </div>
              </div>

              {/* Placement Support Checkbox */}
              <div className="flex items-start space-x-3 bg-white/5 p-4 rounded-xl border border-white/10">
                <Checkbox 
                  id="placement" 
                  checked={formData.placementSupport}
                  onCheckedChange={(checked) => handleChange('placementSupport', checked)}
                  className="mt-1 border-white/30 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                />
                <div>
                  <Label htmlFor="placement" className="text-white font-semibold cursor-pointer">
                    I&apos;m looking for placement support
                  </Label>
                  <p className="text-sm text-gray-400 mt-1">Get assistance in landing your dream job with our 100% placement support</p>
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 rounded-xl text-lg font-semibold transform hover:scale-105 transition-all duration-300 border-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-5 w-5" />
                    Submit Inquiry
                  </>
                )}
              </Button>

              <p className="text-center text-sm text-gray-400">
                We respect your privacy. Your information is secure and will never be shared.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};